import { Suspense } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { GlobalLoadingProvider } from "@/contexts/GlobalLoadingContext";
import { PostHogTracker } from "@/components/PostHogTracker";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PermissionGuard, RedirectToFirstAllowed } from "@/components/PermissionGuard";
import { AppLayout } from "@/components/AppLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { SuperAdminGuard } from "@/components/super-admin/SuperAdminGuard";
import { SupportDeskGuard } from "@/components/support-desk/SupportDeskGuard";
import { SupportDeskLayout } from "@/components/support-desk/SupportDeskLayout";
import { AdminGuard } from "@/components/AdminGuard";
import { VoiceAssistantsGuard } from "@/voice-assistants/components/VoiceAssistantsGuard";
import { GuiaGuard } from "@/components/GuiaGuard";

// Eagerly loaded (auth critical path only)
import Login from "./pages/Login";
import SelectWorkspacePage from "./pages/SelectWorkspacePage";
import { resolveBrand } from "@/config/brand";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { RESERVATIONS_SETUP_ROUTE } from "@/lib/reservations-setup";

// Lazy loaded landing page
const LandingPage = lazyWithRetry(() => import("./pages/LandingPage"));
const FlowLandingPage = lazyWithRetry(() => import("./pages/FlowLandingPage"));

// Lazy loaded page
const Register = lazyWithRetry(() => import("./pages/Register"));
const ChangePasswordPage = lazyWithRetry(() => import("./pages/ChangePasswordPage"));
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const ActivateAccountPage = lazyWithRetry(() => import("./pages/ActivateAccountPage"));
const OAuthConsentPage = lazyWithRetry(() => import("./pages/OAuthConsentPage"));
const WidgetPage = lazyWithRetry(() => import("./pages/WidgetPage"));
const PublicBookingPage = lazyWithRetry(() => import("./pages/PublicBookingPage"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const PrivacyPolicyPage = lazyWithRetry(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazyWithRetry(() => import("./pages/TermsOfServicePage"));
const SmsUpdatesPage = lazyWithRetry(() => import("./pages/SmsUpdatesPage"));
const DataDeletionPage = lazyWithRetry(() => import("./pages/DataDeletionPage"));
const WidgetTestPage = lazyWithRetry(() => import("./pages/WidgetTestPage"));
const MetaOAuthCallback = lazyWithRetry(() => import("./pages/MetaOAuthCallback"));
const DocsPage = lazyWithRetry(() => import("./pages/DocsPage"));
const RfpAnalysisPage = lazyWithRetry(() => import("./pages/RfpAnalysisPage"));
const CapacitacionPage = lazyWithRetry(() => import("./pages/capacitacion/CapacitacionPage"));

const DashboardPage = lazyWithRetry(() => import("./pages/app/DashboardPage"));
const CopilotHomePage = lazyWithRetry(() => import("./pages/app/CopilotHomePage"));
const ConversationsPage = lazyWithRetry(() => import("./pages/app/ConversationsPage"));
const SearchPage = lazyWithRetry(() => import("./pages/app/SearchPage"));
const ContactsPage = lazyWithRetry(() => import("./pages/app/ContactsPage"));
const CampaignsPage = lazyWithRetry(() => import("./pages/app/CampaignsPage"));
const CampaignBlueprintsPage = lazyWithRetry(() => import("./pages/app/CampaignBlueprintsPage"));
const CampaignBlueprintReportsPage = lazyWithRetry(() => import("./pages/app/CampaignBlueprintReportsPage"));
const AutomationsPage = lazyWithRetry(() => import("./pages/app/AutomationsPage"));
const AgentTesterPage = lazyWithRetry(() => import("./pages/app/AgentTesterPage"));
const BusinessUpdatesPage = lazyWithRetry(() => import("./pages/app/BusinessUpdatesPage"));
const ReportsPage = lazyWithRetry(() => import("./pages/app/ReportsPage"));
const ConversationQualityPage = lazyWithRetry(() => import("./pages/app/ConversationQualityPage"));
const ReportsPresencePage = lazyWithRetry(() => import("./pages/app/ReportsPresencePage"));
const ReportsChatVariablesPage = lazyWithRetry(() => import("./pages/app/ReportsChatVariablesPage"));
const ReportsWhatsAppFormsPage = lazyWithRetry(() => import("./pages/app/ReportsWhatsAppFormsPage"));
const ReportsCustomConsultPage = lazyWithRetry(() => import("./pages/app/ReportsCustomConsultPage"));
const ReportsCustomCreatePage = lazyWithRetry(() => import("./pages/app/ReportsCustomCreatePage"));
const ReportsCloseTagsPage = lazyWithRetry(() => import("./pages/app/ReportsCloseTagsPage"));
const ReportsQueuesPage = lazyWithRetry(() => import("./pages/app/ReportsQueuesPage"));
const ReportsDebuggerPage = lazyWithRetry(() => import("./pages/app/ReportsDebuggerPage"));
const CoordinatorTeamPage = lazyWithRetry(() => import("./pages/app/CoordinatorTeamPage"));
const CoordinatorChatPage = lazyWithRetry(() => import("./pages/app/CoordinatorChatPage"));

// VoiceAssistant — Asistentes de Voz IA (feature aislada en src/voice-assistants/)
const VoiceAssistantsPage = lazyWithRetry(() => import("./voice-assistants/pages/VoiceAssistantsPage"));
const CreateVoiceAssistantPage = lazyWithRetry(() => import("./voice-assistants/pages/CreateVoiceAssistantPage"));
const VoiceAssistantDetailPage = lazyWithRetry(() => import("./voice-assistants/pages/VoiceAssistantDetailPage"));
const VoiceCampaignsHubPage = lazyWithRetry(() => import("./voice-assistants/pages/VoiceCampaignsHubPage"));
const TelephonyPage = lazyWithRetry(() => import("./voice-assistants/pages/TelephonyPage"));
// Unified "Bases de conocimiento" (text + voice in one page). Replaces the old
// voice-only KB page, whose route now redirects here.
const SharedKnowledgeBasesPage = lazyWithRetry(() => import("./pages/app/KnowledgeBasesPage"));
const KnowledgeAssistantsAdminPage = lazyWithRetry(() => import("./pages/app/KnowledgeAssistantsAdminPage"));
const AssistantChatPage = lazyWithRetry(() => import("./pages/app/AssistantChatPage"));
const FlowHelpPage = lazyWithRetry(() => import("./pages/app/FlowHelpPage"));
const WhatsAppTemplatesPage = lazyWithRetry(() => import("./pages/app/WhatsAppTemplatesPage"));
const WhatsAppFlowsPage = lazyWithRetry(() => import("./pages/app/WhatsAppFlowsPage"));
// Workspace product catalog (the org-scoped `products` table the search_catalog
// tool reads). Super-admin only while the agent framework migrates to it.
const CatalogPage = lazyWithRetry(() => import("./pages/app/CatalogPage"));
// Workspace business profile + sedes (what buildBusinessContext and
// consultar_sedes read). Super-admin only, same gate as the catalog.
const BusinessPage = lazyWithRetry(() => import("./pages/app/BusinessPage"));
const ReservationsSetupPage = lazyWithRetry(() => import("./pages/app/ReservationsSetupPage"));
// New text-agent editor (DE-448 framework) — UI-only preview, super-admin gated,
// not wired to the backend yet. Reached from an agent card → "Nueva versión".
const SuperAgentTextDetailPage = lazyWithRetry(() => import("./pages/app/SuperAgentTextDetailPage"));
const VoiceAssistantCallPage = lazyWithRetry(() => import("./voice-assistants/pages/VoiceAssistantCallPage"));
const ReportsVoicePage = lazyWithRetry(() => import("./pages/app/ReportsVoicePage"));
const CreateVoiceCampaignPage = lazyWithRetry(() => import("./voice-assistants/pages/CreateVoiceCampaignPage"));
const VoiceCampaignDetailPage = lazyWithRetry(() => import("./voice-assistants/pages/VoiceCampaignDetailPage"));
const SettingsAppPage = lazyWithRetry(() => import("./pages/app/SettingsAppPage"));
const SubscriptionSuccessPage = lazyWithRetry(() => import("./pages/app/SubscriptionSuccessPage"));
const ResolveAccountPage = lazyWithRetry(() => import("./pages/app/ResolveAccountPage"));
const HelpPage = lazyWithRetry(() => import("./pages/app/HelpPage"));
const VideoTutorialsPage = lazyWithRetry(() => import("./pages/app/VideoTutorialsPage"));
const ReservationsPage = lazyWithRetry(() => import("./pages/app/ReservationsPage"));
const ReservationConfirmationPage = lazyWithRetry(() => import("./pages/app/ReservationConfirmationPage"));
const ReservationFloorPage = lazyWithRetry(() => import("./pages/app/ReservationFloorPage"));
const ReservationCalendarPage = lazyWithRetry(() => import("./pages/app/ReservationCalendarPage"));
const IncompleteReservationsPage = lazyWithRetry(() => import("./pages/app/IncompleteReservationsPage"));
const ReservationReportsPage = lazyWithRetry(() => import("./pages/app/ReservationReportsPage"));
const OnboardingPage = lazyWithRetry(() => import("./pages/app/OnboardingPage"));
const AIAgentOnboardingPage = lazyWithRetry(() => import("./pages/app/AIAgentOnboardingPage"));

const SADashboardPage = lazyWithRetry(() => import("./pages/super-admin/SADashboardPage"));
const SAAccountsPage = lazyWithRetry(() => import("./pages/super-admin/SAAccountsPage"));
const SAAccountsByPlanPage = lazyWithRetry(() => import("./pages/super-admin/SAAccountsByPlanPage"));
const SASubscriptionsPage = lazyWithRetry(() => import("./pages/super-admin/SASubscriptionsPage"));
const SAUsersPage = lazyWithRetry(() => import("./pages/super-admin/SAUsersPage"));
const SAFinancialsLayout = lazyWithRetry(() => import("./pages/super-admin/financials/SAFinancialsLayout"));
const SACostsPage = lazyWithRetry(() => import("./pages/super-admin/financials/SACostsPage"));
const SARevenuePage = lazyWithRetry(() => import("./pages/super-admin/financials/SARevenuePage"));
const SAPnLPage = lazyWithRetry(() => import("./pages/super-admin/financials/SAPnLPage"));
const SABalancePage = lazyWithRetry(() => import("./pages/super-admin/financials/SABalancePage"));
const SAOrgBalancePage = lazyWithRetry(() => import("./pages/super-admin/financials/SAOrgBalancePage"));
const SASaaSMetricsPage = lazyWithRetry(() => import("./pages/super-admin/financials/SASaaSMetricsPage"));
const SAAIReconciliationPage = lazyWithRetry(() => import("./pages/super-admin/financials/SAAIReconciliationPage"));
const SACostCoveragePage = lazyWithRetry(() => import("./pages/super-admin/financials/SACostCoveragePage"));
const SAEmailsPage = lazyWithRetry(() => import("./pages/super-admin/SAEmailsPage"));
const SAAnnouncementsPage = lazyWithRetry(() => import("./pages/super-admin/SAAnnouncementsPage"));
const SAAnnouncementDetailPage = lazyWithRetry(() => import("./pages/super-admin/SAAnnouncementDetailPage"));
const NewsPage = lazyWithRetry(() => import("./pages/NewsPage"));
const SASettingsPage = lazyWithRetry(() => import("./pages/super-admin/SASettingsPage"));
const SALoadTestsPage = lazyWithRetry(() => import("./pages/super-admin/SALoadTestsPage"));
const SACronHealthPage = lazyWithRetry(() => import("./pages/super-admin/SACronHealthPage"));
const SAAvailabilityPage = lazyWithRetry(() => import("./pages/super-admin/SAAvailabilityPage"));
const SAPlansPage = lazyWithRetry(() => import("./pages/super-admin/SAPlansPage"));
const SAExtraPacksPage = lazyWithRetry(() => import("./pages/super-admin/SAExtraPacksPage"));
const SACreditsPage = lazyWithRetry(() => import("./pages/super-admin/SACreditsPage"));
const SAMercadoPagoPage = lazyWithRetry(() => import("./pages/super-admin/SAMercadoPagoPage"));
const SAFlowAdsPage = lazyWithRetry(() => import("./pages/super-admin/SAFlowAdsPage"));
const SAFlowAdsOAuthCallback = lazyWithRetry(() => import("./pages/super-admin/SAFlowAdsOAuthCallback"));
const SAApiKeysPage = lazyWithRetry(() => import("./pages/super-admin/SAApiKeysPage"));
const SAEncryptionPage = lazyWithRetry(() => import("./pages/super-admin/SAEncryptionPage"));
const SASupportPage = lazyWithRetry(() => import("./pages/super-admin/SASupportPage"));
const SupportDeskPage = lazyWithRetry(() => import("./pages/support-desk/SupportDeskPage"));
// Agent Framework admin (DE-436): the vertical blueprints and the platform
// prompts they compose with. Until now both were SQL-only.
const SABlueprintsPage = lazyWithRetry(() => import("./pages/super-admin/SABlueprintsPage"));
const SAPromptsPage = lazyWithRetry(() => import("./pages/super-admin/SAPromptsPage"));
const SupportPage = lazyWithRetry(() => import("./pages/app/SupportPage"));

const currentBrand = resolveBrand();
const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner position="top-right" />
      <BrowserRouter>
        <BrandProvider>
        <GlobalLoadingProvider>
        <AuthProvider>
          <PostHogTracker />
          <Suspense fallback={null}>
          <Routes>
            {/* Public pages */}
            <Route path="/" element={<FlowLandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/select-workspace" element={<SelectWorkspacePage />} />
            <Route path="/register" element={currentBrand.id === "flow" ? <Register /> : <Navigate to="/login" replace />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/oauth/consent" element={<OAuthConsentPage />} />
            <Route path="/activate" element={<ActivateAccountPage />} />
            <Route path="/widget" element={<WidgetPage />} />
            <Route path="/reservar/:slug" element={<PublicBookingPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/sms" element={<SmsUpdatesPage />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />
            <Route path="/widget-test" element={<WidgetTestPage />} />
            <Route path="/auth/meta/callback" element={<MetaOAuthCallback />} />
            {/* Flow Ads OAuth popup callback for the org-admin (/app/ads) flow.
                Public like /auth/meta/callback — it only relays the code to the
                opener window. The super-admin flow uses its own callback under
                /super-admin/ads/oauth/callback. */}
            <Route path="/auth/meta-ads/callback" element={<SAFlowAdsOAuthCallback />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/rfp-analysis" element={<RfpAnalysisPage />} />
            <Route path="/capacitacion" element={<CapacitacionPage />} />

            {/* Protected app routes */}
            <Route path="/app" element={<ProtectedRoute />}>
              {/* Onboarding — outside AppLayout for full-screen experience */}
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="onboarding-ai-agent" element={<AIAgentOnboardingPage />} />
              <Route element={<AppLayout />}>
                <Route path="inicio" element={<CopilotHomePage />} />
                <Route path="dashboard" element={<PermissionGuard permissionKey="dashboard"><DashboardPage /></PermissionGuard>} />
                <Route path="conversations" element={<PermissionGuard permissionKey="conversations"><ConversationsPage /></PermissionGuard>} />
                <Route path="search" element={<SearchPage />} />
                <Route path="contacts" element={<PermissionGuard permissionKey="contacts"><ContactsPage /></PermissionGuard>} />
                <Route path="campaigns" element={<PermissionGuard permissionKey="campaigns.text"><CampaignsPage /></PermissionGuard>} />
                <Route path="campaign-blueprints" element={<PermissionGuard permissionKey="campaigns.blueprints"><CampaignBlueprintsPage /></PermissionGuard>} />
                <Route path="automations" element={<PermissionGuard permissionKey="automations"><AutomationsPage /></PermissionGuard>} />
                <Route path="agent-tester" element={<AgentTesterPage />} />
                <Route path="updates" element={<PermissionGuard permissionKey="updates"><BusinessUpdatesPage /></PermissionGuard>} />
                <Route path="reports" element={<PermissionGuard permissionKey="reports"><ReportsPage /></PermissionGuard>} />
                <Route path="reports/quality" element={<PermissionGuard permissionKey="reports"><ConversationQualityPage /></PermissionGuard>} />
                <Route path="reports/presence" element={<PermissionGuard permissionKey="reports"><ReportsPresencePage /></PermissionGuard>} />
                <Route path="reports/chat-variables" element={<PermissionGuard permissionKey="reports.chat_variables"><ReportsChatVariablesPage /></PermissionGuard>} />
                <Route path="reports/whatsapp-forms" element={<PermissionGuard permissionKey="reports.whatsapp_forms"><ReportsWhatsAppFormsPage /></PermissionGuard>} />
                <Route path="reports/custom" element={<PermissionGuard permissionKey="reports.custom_view"><ReportsCustomConsultPage /></PermissionGuard>} />
                <Route path="reports/custom/create" element={<PermissionGuard permissionKey="reports.custom_create"><ReportsCustomCreatePage /></PermissionGuard>} />
                <Route path="reports/close-tags" element={<PermissionGuard permissionKey="reports.close_tags"><ReportsCloseTagsPage /></PermissionGuard>} />
                <Route path="reports/queues" element={<PermissionGuard permissionKey="reports.queues"><ReportsQueuesPage /></PermissionGuard>} />
                <Route path="reports/debugger" element={<PermissionGuard permissionKey="reports.debugger"><ReportsDebuggerPage /></PermissionGuard>} />
                <Route path="reports/campaign-blueprints" element={<PermissionGuard permissionKey="campaigns.blueprints.reports"><CampaignBlueprintReportsPage /></PermissionGuard>} />
                <Route path="coordinator/team" element={<CoordinatorTeamPage />} />
                <Route path="coordinator/chat" element={<CoordinatorChatPage />} />
                <Route path="knowledge-bases" element={<PermissionGuard permissionKey="knowledge_bases"><SharedKnowledgeBasesPage /></PermissionGuard>} />
                <Route element={<GuiaGuard />}>
                  <Route path="knowledge-assistants" element={<PermissionGuard permissionKey="knowledge_assistants.manage"><KnowledgeAssistantsAdminPage /></PermissionGuard>} />
                  <Route path="assistant" element={<PermissionGuard permissionKey="knowledge_assistants.consult"><AssistantChatPage /></PermissionGuard>} />
                  <Route path="flow-help" element={<PermissionGuard permissionKey="platform_assistant.consult"><FlowHelpPage /></PermissionGuard>} />
                </Route>
                <Route path="whatsapp-templates" element={<PermissionGuard permissionKey="whatsapp_templates"><WhatsAppTemplatesPage /></PermissionGuard>} />
                <Route path="whatsapp-flows" element={<PermissionGuard permissionKey="whatsapp_templates"><WhatsAppFlowsPage /></PermissionGuard>} />
                {/* Negocio y Catálogo son el contenido que el runtime lee en vivo en
                    cada mensaje, así que los administra el dueño del workspace, no
                    nosotros. Ambas pantallas ya trabajan contra tablas con RLS por
                    organización (products, business_profiles, business_locations). */}
                <Route element={<AdminGuard />}>
                  <Route path="catalog" element={<CatalogPage />} />
                </Route>
                <Route element={<AdminGuard orPermission="reservations" orPermissionNeedsTables />}>
                  <Route path="business" element={<BusinessPage />} />
                </Route>
                <Route element={<AdminGuard orPermission="reservations" />}>
                  <Route path={RESERVATIONS_SETUP_ROUTE} element={<ReservationsSetupPage />} />
                </Route>
                {/* New text-agent editor (DE-448). No longer super-admin gated: it is
                    where the agent-creation flow lands, so every customer needs it. */}
                <Route path="agents/:id/nuevo-editor" element={<PermissionGuard permissionKey="automations"><SuperAgentTextDetailPage /></PermissionGuard>} />
                {/* Flow Ads — org admins manage their own org's Meta Ads account.
                    Reuses the super-admin Ads UI; the backend scopes every account
                    and campaign to the caller's organization. */}
                <Route element={<AdminGuard />}>
                  <Route path="ads" element={<SAFlowAdsPage />} />
                </Route>
                {/* Voice assistants — super-admins + any org with a Motor de Voz account,
                    plus per-section RBAC keys (voice_assistants.*) */}
                <Route element={<VoiceAssistantsGuard />}>
                  {/* Voice assistants are listed alongside the text ones in the
                      default tab; there is no separate "voice" tab, and asking
                      for one left the page blank. */}
                  <Route path="voice-assistants" element={<Navigate to="/app/automations" replace />} />
                  <Route path="voice-assistants/nuevo" element={<PermissionGuard permissionKey="voice_assistants"><CreateVoiceAssistantPage /></PermissionGuard>} />
                  <Route path="voice-assistants/campaigns" element={<PermissionGuard permissionKey="campaigns.voice"><VoiceCampaignsHubPage /></PermissionGuard>} />
                  <Route path="voice-assistants/knowledge-bases" element={<Navigate to="/app/knowledge-bases" replace />} />
                  {/* Voice stats live under Reportes → "Reportes Voz" (/app/reports/voice).
                      Gated by the voice module (VoiceAssistantsGuard) + the `reports`
                      permission. Keep the old path as a redirect so bookmarks don't break. */}
                  <Route path="reports/voice" element={<PermissionGuard permissionKey="reports"><ReportsVoicePage /></PermissionGuard>} />
                  <Route path="voice-assistants/estadisticas" element={<Navigate to="/app/reports/voice" replace />} />
                  {/* Voice contacts moved into the unified Contactos page. Keep the old
                      path as a redirect so bookmarks/links don't break. */}
                  <Route path="voice-assistants/contactos" element={<Navigate to="/app/contacts" replace />} />
                  <Route path="voice-assistants/calls/:id" element={<PermissionGuard permissionKey="reports"><VoiceAssistantCallPage /></PermissionGuard>} />
                  <Route path="voice-assistants/telephony" element={<PermissionGuard permissionKey="settings.telephony"><TelephonyPage /></PermissionGuard>} />
                  <Route path="voice-assistants/campaigns/nueva" element={<PermissionGuard permissionKey="campaigns.voice"><CreateVoiceCampaignPage /></PermissionGuard>} />
                  <Route path="voice-assistants/campaigns/:id/editar" element={<PermissionGuard permissionKey="campaigns.voice"><CreateVoiceCampaignPage /></PermissionGuard>} />
                  <Route path="voice-assistants/campaigns/:id" element={<PermissionGuard permissionKey="campaigns.voice"><VoiceCampaignDetailPage /></PermissionGuard>} />
                  <Route path="voice-assistants/:id" element={<PermissionGuard permissionKey="voice_assistants"><VoiceAssistantDetailPage /></PermissionGuard>} />
                </Route>
                <Route path="reservations" element={<Navigate to="/app/reservations/confirmation" replace />} />
                <Route path="reservations/confirmation" element={<PermissionGuard permissionKey="reservations"><ReservationConfirmationPage /></PermissionGuard>} />
                <Route path="reservations/floor" element={<PermissionGuard permissionKey="reservations"><ReservationFloorPage /></PermissionGuard>} />
                <Route path="reservations/calendar" element={<PermissionGuard permissionKey="reservations"><ReservationCalendarPage /></PermissionGuard>} />
                <Route path="reservations/incomplete" element={<PermissionGuard permissionKey="reservations"><IncompleteReservationsPage /></PermissionGuard>} />
                <Route path="reservations/reports" element={<PermissionGuard permissionKey="reservations"><ReservationReportsPage /></PermissionGuard>} />
                <Route path="subscription-success" element={<SubscriptionSuccessPage />} />
                <Route path="billing/resolve" element={<ResolveAccountPage />} />
                <Route path="settings" element={<Navigate to="/app/settings/channels" replace />} />
                <Route path="settings/:tab" element={<PermissionGuard permissionKey="settings"><SettingsAppPage /></PermissionGuard>} />
                <Route path="help" element={<PermissionGuard permissionKey="help"><HelpPage /></PermissionGuard>} />
                <Route path="help/videos" element={<PermissionGuard permissionKey="help"><VideoTutorialsPage /></PermissionGuard>} />
                <Route path="support" element={<PermissionGuard permissionKey="support"><SupportPage /></PermissionGuard>} />
                <Route path="news" element={<NewsPage />} />
                <Route index element={<RedirectToFirstAllowed />} />
              </Route>
            </Route>

            {/* Super Admin routes */}
            <Route path="/super-admin" element={<ProtectedRoute />}>
              <Route element={<SuperAdminGuard />}>
                <Route element={<SuperAdminLayout />}>
                  <Route index element={<SADashboardPage />} />
                  <Route path="accounts" element={<SAAccountsPage />} />
                  <Route path="accounts/by-plan" element={<SAAccountsByPlanPage />} />
                  <Route path="subscriptions" element={<SASubscriptionsPage />} />
                  <Route path="financials" element={<SAFinancialsLayout />}>
                    <Route index element={<Navigate to="/super-admin/financials/costs" replace />} />
                    <Route path="costs" element={<SACostsPage />} />
                    <Route path="revenue" element={<SARevenuePage />} />
                    <Route path="pnl" element={<SAPnLPage />} />
                    <Route path="balance" element={<SABalancePage />} />
                    <Route path="org-balance" element={<SAOrgBalancePage />} />
                    <Route path="saas" element={<SASaaSMetricsPage />} />
                    <Route path="ai-reconciliation" element={<SAAIReconciliationPage />} />
                    <Route path="coverage" element={<SACostCoveragePage />} />
                  </Route>
                  <Route path="users" element={<SAUsersPage />} />
                  <Route path="api-keys" element={<SAApiKeysPage />} />
                  <Route path="encryption" element={<SAEncryptionPage />} />
                  <Route path="support" element={<SASupportPage />} />
                  <Route path="emails" element={<SAEmailsPage />} />
                  <Route path="announcements" element={<SAAnnouncementsPage />} />
                  <Route path="announcements/:id" element={<SAAnnouncementDetailPage />} />
                  <Route path="blueprints" element={<SABlueprintsPage />} />
                  <Route path="prompts" element={<SAPromptsPage />} />
                  <Route path="load-tests" element={<SALoadTestsPage />} />
                  <Route path="cron-health" element={<SACronHealthPage />} />
                  <Route path="availability" element={<SAAvailabilityPage />} />
                  <Route path="plans" element={<SAPlansPage />} />
                  <Route path="extra-packs" element={<SAExtraPacksPage />} />
                  <Route path="credits" element={<SACreditsPage />} />
                  <Route path="mercadopago" element={<SAMercadoPagoPage />} />
                  <Route path="ads" element={<SAFlowAdsPage />} />
                  <Route path="ads/oauth/callback" element={<SAFlowAdsOAuthCallback />} />
                  <Route path="settings/:section" element={<SASettingsPage />} />
                  <Route path="settings" element={<Navigate to="/super-admin/settings/general" replace />} />
                </Route>
              </Route>
            </Route>

            <Route path="/support-desk" element={<SupportDeskGuard />}>
              <Route element={<SupportDeskLayout />}>
                <Route index element={<SupportDeskPage />} />
              </Route>
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AuthProvider>
        </GlobalLoadingProvider>
        </BrandProvider>
      </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
  </ErrorBoundary>
);

export default App;
